import { Skeleton } from 'antd'
import classNames from 'classnames'
import { useAppSelector } from 'common/hooks/redux'
import {
  useFetchSignedDocumentsQuery,
  useFetchPhotoStreamQuery,
} from 'features/Procedure/state/procedure.api'
import { useEffect, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { useParams } from 'react-router'
import styles from './preview.module.scss'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

type Props = {
  selectedPatientId: number | null
  scrollMode?: 'self' | 'parent'
}

export const FormsPreview = ({ selectedPatientId, scrollMode = 'self' }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const selectedFormDocId = useAppSelector((state) => state.procedureReducer.selectedFormDocId)

  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)

  const shouldSkipFormsQuery = !selectedPatientId

  const { isFetching: isFetchingForms, isLoading: isLoadingForms } = useFetchSignedDocumentsQuery(
    {
      patientId: selectedPatientId!,
    },
    {
      skip: shouldSkipFormsQuery,
    },
  )

  const { data: pdfBlob, isFetching: isFetchingPdf } = useFetchPhotoStreamQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      documentId: selectedFormDocId!,
    },
    {
      skip: !selectedFormDocId || !siteId || !selectedPatientId,
    },
  )

  useEffect(() => {
    if (pdfBlob && pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob)
      setPdfUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setPdfUrl(null)
    }
  }, [pdfBlob])

  useEffect(() => {
    if (!selectedFormDocId) {
      setPdfUrl(null)
      setNumPages(null)
    }
  }, [selectedFormDocId])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error)
  }

  if (isFetchingForms || isLoadingForms || isFetchingPdf) {
    return (
      <div className={styles.container}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    )
  }

  if (!selectedFormDocId) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <span>No document selected</span>
        </div>
      </div>
    )
  }

  if (!pdfUrl) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>Failed to load document</div>
      </div>
    )
  }

  return (
    <Document
      file={pdfUrl}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadError={onDocumentLoadError}
      loading={<Skeleton active paragraph={{ rows: 12 }} />}
      className={classNames(styles.document, {
        [styles.documentParentScroll]: scrollMode === 'parent',
      })}>
      <div className={styles.pageWrapper}>
        {Array.from(new Array(numPages), (_, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            className={styles.page}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            width={undefined}
          />
        ))}
      </div>
    </Document>
  )
}
